import { AppIcon } from '@/components/ui/app-icon';
import * as React from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { iconCheck, iconCopy } from '@/lib/icons';

/**
 * CopyButton - icon-to-checkmark animation primitive.
 *
 * Replaces the ~25 `toast.success('Copied!')` calls scattered through the
 * codebase. Clay never fires a toast for a copy - the button itself is the
 * feedback. Click → icon swaps to a green check for ~1.5s → back to copy.
 *
 * Two shapes:
 *  - inline icon-only button (most common)
 *  - inline icon + "Copy" label (use when there's room)
 */
export interface CopyButtonProps {
  value: string;
  label?: string;
  tooltip?: string;
  copiedTooltip?: string;
  size?: 'sm' | 'md';
  className?: string;
  onCopy?: () => void;
  'aria-label'?: string;
}

export function CopyButton({
  value,
  label,
  tooltip = 'Copy',
  copiedTooltip = 'Copied',
  size = 'md',
  className,
  onCopy,
  'aria-label': ariaLabel,
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);
  const timeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleClick = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        onCopy?.();
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to copy to clipboard';
        console.error('CopyButton: clipboard write failed', error);
        toast.error(message);
      }
    },
    [value, onCopy]
  );

  const iconPx = size === 'sm' ? 12 : 14;
  const buttonSize =
    size === 'sm' ? 'h-6 px-1.5 text-[11px]' : 'h-7 px-2 text-[12px]';

  const button = (
    <button
      type='button'
      onClick={handleClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded text-muted-foreground transition-colors duration-150 hover:bg-muted/60 hover:text-foreground focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-hidden',
        label
          ? buttonSize
          : size === 'sm'
            ? 'h-6 w-6 justify-center'
            : 'h-7 w-7 justify-center',
        className
      )}
      aria-label={ariaLabel ?? tooltip}
    >
      {copied ? (
        <AppIcon
          icon={iconCheck}
          className='text-emerald-500'
          style={{ fontSize: iconPx }}
        />
      ) : (
        <AppIcon icon={iconCopy} style={{ fontSize: iconPx }} />
      )}
      {label && <span>{copied ? 'Copied' : label}</span>}
    </button>
  );

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side='top'>
        {copied ? copiedTooltip : tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
